import React, { useEffect, useState } from 'react';
import { cubicBezier, motion } from 'framer-motion';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

type TextChild = {
  text: string;
};

type Paragraph = {
  children: TextChild[];
};

type Service = {
  heading?: string;
  content?: Paragraph[];
};

type Block = {
  __typename: string;
  heading?: string;
  [key: string]: unknown;
};

type Padding = {
  mobile_top_padding: any;
  mobile_bottom_padding: any;
  desktop_top_padding: any;
  desktop_bottom_padding: any;
};

type Page = {
  name: string;
  short_description: string | null;
  page_color: string;
  content: string;
  blocks: Block[];
  services?: Service[];
  padding?: Padding;
  __typename: string;
};

type Props = {
  data: Page;
  /** Optional overrides for padding, if you want to set from outside */
  overridePaddingTop?: number;
  overridePaddingBottom?: number;
  /** If true, hide the border lines above the service blocks */
  hideBorders?: boolean;
};

type BlocksRendererProps = React.ComponentProps<typeof BlocksRenderer>;
const ComponentSectionsOurServices: React.FC<Props> = ({ data, hideBorders = false }) => {
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };
  const easing = cubicBezier(0.77, 0, 0.175, 1);
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: easing },
    },
  };

  const services = data?.services ?? [];

  // Calculate padding with overrides and defaults
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <motion.section
      id={data?.__typename}
      className="grid pt-4 md:pt-56 font-light"
      style={{
        paddingTop: `${top}rem`,
        paddingBottom: `${bottom}rem`,
      }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      {services.map((service, index) => (
        <motion.div
          key={index}
          id={`${data?.__typename}-service-${index}`}
          className={`grid grid-cols-12 items-start pt-4 md:pt-5 pb-16 md:pb-20 gap-3 md:gap-4 step-sec ${
            hideBorders ? '' : 'border-t border-[#2B332A66] border-dynamic'
          }`}
          variants={itemVariants}
        >
          <div className="col-span-12 md:col-span-6">
            <h1 className="text-xl md:text-4xl font-light !leading-none">
              {String(index + 1).padStart(2, '0')}
            </h1>
          </div>
          <div className="col-span-12 md:col-span-6 right-content blocksRenderer">
            <h2 className="text-xl md:text-[22px] font-light leading-none mb-6">
              {service.heading}
            </h2>
            <div className="font-light indent-16">
              <BlocksRenderer content={service.content as BlocksRendererProps['content']} />
            </div>

            {/* {service.content?.map((para, paraIdx) => (
              <p key={paraIdx} className="font-light indent-10">
                {para.children?.map((child, childIdx) => (
                  <motion.span
                    key={childIdx}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: childIdx * 0.5, duration: 1 }}
                  >
                    {child.text}
                  </motion.span>
                ))}
              </p>
            ))} */}
          </div>
        </motion.div>
      ))}
    </motion.section>
  );
};

export default ComponentSectionsOurServices;
